import type * as Electron from 'electron'
import {logger, Util} from 'yougile-util'
import {HubProperties} from 'yougile-model'
import {Backend} from '../../common/backend'
import {globals} from '../extensions/globals-base'
import {PageService} from '../extensions/page-service'
import {Refresh} from './refresh'
import {Sync} from './sync'

export class DesktopRenderer {
  private static allowUnload = false
  private static electron: {
    ipcRenderer: Electron.IpcRenderer
  }

  public static trackUsage(): void {
    const profile = Sync.getProfile()
    if (!profile) {
      logger.error('Profile not found.')
      return
    }
    const profileHub = profile.get(profile.companyId)
    if (!profileHub.properties[HubProperties.usedDesktopApp]) {
      profileHub.properties[HubProperties.usedDesktopApp] = true
      profileHub.commit()
    }
  }

  public static notify(): void {
    if (Util.isDesktop() && this.electron) {
      this.electron.ipcRenderer.send('notify')
    }
  }

  public static showApp(): void {
    const {ipcRenderer} = this.electron
    ipcRenderer.send('show-app')
  }

  public static reqDesktopVersionUpdate(): void {
    const {ipcRenderer} = this.electron
    ipcRenderer.send('req-desktop-version')
  }

  public static init(): void {
    if (!Util.isDesktop) {
      logger.warn('Initializing desktop renderer on not desktop environment')
    }
    const wnd = window as any
    this.electron = wnd.require('electron')
    delete wnd.require
    delete wnd.module

    window.onbeforeunload = (e: BeforeUnloadEvent) => {
      if (DesktopRenderer.allowUnload) {
        logger.log('Allowing unload')
        return
      }
      if (localStorage.getItem('user_id')) {
        logger.log('Preventing unload')
        e.returnValue = false
      }
    }
    PageService.setUrl('/team/')
    const {ipcRenderer} = this.electron

    ipcRenderer.once('clear-and-reload', () => {
      localStorage.clear()
      ipcRenderer.send('forced-reload')
    })

    ipcRenderer.on('logout', () => {
      Refresh.clearCredentials()
    })

    ipcRenderer.on('desktop-version', (_: unknown, arg: unknown) => {
      if (arg && typeof arg === 'string') {
        globals.desktopVersion = arg
        logger.log('ipcRenderer, set desktopVersion', arg)
      }
    })

    Refresh.init(
      () => {
        DesktopRenderer.allowUnload = true
        ipcRenderer.send('reload', {
          type: 'saved-user',
          value: localStorage.getItem('user_id')
        })
        ipcRenderer.once('reload-done', () => {
          DesktopRenderer.allowUnload = false
        })
      },
      async () => {
        const key = Backend.loadKey()
        Backend.clearCredentials()
        await Sync.invalidateCache()
        await Backend.logout(key)
        logger.log('Refresh init: reload')
        Refresh.refresh()
      }
    )

    document.addEventListener('keyup', e => {
      if (e.code === 'KeyR' && e.altKey && e.ctrlKey) {
        Refresh.refresh()
      }
    })

    ipcRenderer.on('allow-unload', () => {
      DesktopRenderer.allowUnload = true
      ipcRenderer.send('allow-unload-done')
    })

    ipcRenderer.on(
      'badge',
      (_: Electron.IpcRendererEvent, obj: {value: string}) => {
        ipcRenderer.send('badge', {
          data: DesktopRenderer.drawBadge(obj.value),
          text: obj.value
        })
      }
    )
  }

  public static drawBadge(text: string): string {
    const canvas = document.createElement('canvas')
    canvas.width = 32
    canvas.height = 32
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return ''
    }
    ctx.fillStyle = '#f30868'
    ctx.beginPath()
    ctx.arc(16, 16, 16, 0, 2 * Math.PI)
    ctx.fill()
    ctx.fillStyle = '#fff'
    ctx.font = '21px Arial'
    ctx.textAlign = 'center'
    ctx.fillText(text, 16, 23)
    return canvas.toDataURL('image/png')
  }

  public static focus(): void {
    this.electron.ipcRenderer.send('focus-window')
  }

  public static setCredCookies(
    key: string,
    value: string | undefined,
    expiration: number,
    host: string
  ) {
    this.electron.ipcRenderer.send('set-cookie', {key, value, expiration, host})
  }
}
